"use client";

import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import Link from "next/link";
import { Briefcase, X, ArrowRight } from "lucide-react";
import DOMPurify from "dompurify";
import HomePageLayout from "@/components/layout/HomePageLayout";
import DesignerApplicationForm from "@/components/application/DesignerApplicationForm";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export interface ApplyContext {
  code: string;
  jobTitle: string;
  companyName: string | null;
  companyLogoUrl?: string | null;
  country?: string | null;
  /** Job description HTML (version-matched) — shown read-only on demand */
  jdHtml?: string | null;
  /** Published JD version shown on this page — recorded on the application */
  jdVersionId?: string | null;
}

interface ApplicationPageProps {
  applyContext?: ApplyContext;
}

function ApplicationPageContent({ applyContext }: ApplicationPageProps) {
  const [email, setEmail] = useState("");
  const [existingApplicationEmail, setExistingApplicationEmail] = useState<string | null>(null);
  const [jdOpen, setJdOpen] = useState(false);
  const focusApplicationOnClose = useRef(false);

  // Recruiter-authored JD HTML, sanitized anyway — this page is public.
  // Sanitize only once the drawer opens: DOMPurify needs a browser window,
  // and running it during SSR throws (sanitize is undefined server-side).
  const hasJd = !!applyContext?.jdHtml;
  const safeJdHtml = useMemo(
    () =>
      jdOpen && applyContext?.jdHtml
        ? DOMPurify.sanitize(applyContext.jdHtml)
        : null,
    [jdOpen, applyContext?.jdHtml]
  );

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  // Check only complete addresses after typing pauses. Ignore responses for an
  // older address so a slow request cannot replace the current form state.
  useEffect(() => {
    const normalizedEmail = email.trim().toLowerCase();
    setExistingApplicationEmail(null);
    if (applyContext || !z.string().email().safeParse(normalizedEmail).success) return;

    const controller = new AbortController();
    let cancelled = false;
    const timeout = window.setTimeout(async () => {
      try {
        const response = await fetch(
          `/api/application/check?email=${encodeURIComponent(normalizedEmail)}`,
          { signal: controller.signal }
        );
        if (!response.ok) return;
        const result = await response.json();
        if (!cancelled && result.success && result.hasApplied) {
          setExistingApplicationEmail(normalizedEmail);
        }
      } catch (error) {
        if (!cancelled) console.error("Error checking application:", error);
      }
    }, 400);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [email, applyContext]);

  const duplicateEmail = existingApplicationEmail === email.trim().toLowerCase()
    ? existingApplicationEmail
    : null;

  return (
    <HomePageLayout>
      <div className="min-h-screen bg-wcd-pricing pt-1 pb-16 md:pb-24">
        {/* Single white container for hero and form */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          {/* Job context (only when arriving via an apply link) — breadcrumb-pill
              style, matching the platform's shortlist header chip */}
          {applyContext && (
            <Dialog open={jdOpen} onOpenChange={setJdOpen}>
            <div className="mt-6">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500">You are applying for</span>
                  {/* Same compact pill as the platform's breadcrumb */}
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-black/[0.03] border border-black/[0.06] px-2.5 py-0.5">
                    {applyContext.companyLogoUrl && (
                      <img
                        src={applyContext.companyLogoUrl}
                        alt={applyContext.companyName ?? ""}
                        className="h-4 w-4 rounded-full object-cover"
                      />
                    )}
                    {applyContext.companyName && (
                      <>
                        <span className="text-xs text-black/40">{applyContext.companyName}</span>
                        <span className="text-black/30">&middot;</span>
                      </>
                    )}
                    <span className="text-xs font-medium text-black/70">{applyContext.jobTitle}</span>
                  </span>
                </div>
                {hasJd && (
                  /* Same look as the platform's header buttons (e.g. Apply links) */
                  <DialogTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-black/10 bg-white px-3 py-1.5 text-sm text-gray-800 cursor-pointer hover:border-black/25 transition-colors"
                  >
                    {/* Same icon as the platform sidebar's Job descriptions entry */}
                    <Briefcase className="h-3.5 w-3.5" />
                    View job description
                  </button>
                  </DialogTrigger>
                )}
              </div>
              {/* Job description side drawer — half the screen, full read of the
                  live JD (fetched fresh from the shared DB on every page load) */}
              {hasJd && (
                <DialogContent
                  aria-describedby={undefined}
                  onCloseAutoFocus={(event) => {
                    if (!focusApplicationOnClose.current) return;
                    event.preventDefault();
                    focusApplicationOnClose.current = false;
                    const form = document.getElementById("designer-application-form");
                    const target = form?.querySelector<HTMLElement>("input, button, a") || form;
                    target?.focus({ preventScroll: true });
                    form?.scrollIntoView({
                      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
                      block: "start",
                    });
                  }}
                  className="left-auto right-0 top-0 z-[70] flex h-dvh w-full max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden bg-white shadow-2xl md:w-1/2 min-w-[min(100vw,480px)] data-[state=open]:animate-none data-[state=closed]:animate-none"
                >
                    {/* Same layout as the platform's internal Job Description
                        page (title row + metadata bar + content card), minus
                        the editing actions (Generate/Comments/Share/Save) —
                        this face is public and read-only. */}
                    <div className="flex items-center justify-between px-6 py-4">
                      <DialogTitle className="text-lg font-bold text-gray-900">Job Description</DialogTitle>
                      <button
                        type="button"
                        onClick={() => setJdOpen(false)}
                        aria-label="Close job description"
                        className="p-2 text-gray-500 hover:text-gray-900 bg-transparent border-none cursor-pointer"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>

                    {/* min-h-0 lets this flex child shrink so it scrolls
                        instead of silently overflowing the panel. The metadata
                        bar lives inside the scroll container so it shares the
                        exact same width as the content card (same gutters,
                        same scrollbar inset). */}
                    <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 pb-4">
                    {/* Metadata bar — mirrors the internal page's Company/Name/Country card */}
                    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
                      <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                          {applyContext.companyLogoUrl ? (
                            <img
                              src={applyContext.companyLogoUrl}
                              alt=""
                              className="w-8 h-8 rounded object-cover flex-shrink-0"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-medium text-gray-400">
                                {(applyContext.companyName || "C").charAt(0)}
                              </span>
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="text-xs text-gray-500">Company</div>
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {applyContext.companyName || "Confidential"}
                            </div>
                          </div>
                        </div>

                        <div className="w-px h-8 bg-gray-200 flex-shrink-0" />

                        <div className="min-w-0 flex-1">
                          <div className="text-xs text-gray-500">Position</div>
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {applyContext.jobTitle}
                          </div>
                        </div>

                        {applyContext.country && (
                          <>
                            <div className="w-px h-8 bg-gray-200 flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <div className="text-xs text-gray-500">Country</div>
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {applyContext.country}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="mt-4">
                      {/* Same width as the metadata bar above (both share the
                          scroll container's gutters and scrollbar inset) */}
                      <div className="rounded-lg border border-gray-200 bg-white px-6 py-5">
                        <div
                          className="prose prose-sm max-w-none text-gray-700"
                          // Our own JD editor content, rendered read-only for context
                          dangerouslySetInnerHTML={{ __html: safeJdHtml || "" }}
                        />
                      </div>
                    </div>
                    </div>
                    {/* The application-flow JD view links straight to applying —
                        unlike the customer review/comment share, which never does */}
                    <div className="px-6 py-4 border-t border-black/[0.06] bg-gray-50 flex justify-end">
                      <button
                        type="button"
                        onClick={() => {
                          focusApplicationOnClose.current = true;
                          setJdOpen(false);
                        }}
                        className="text-sm font-medium text-white bg-black rounded-full px-5 py-2 border-none cursor-pointer hover:bg-black/80"
                      >
                        Apply now
                      </button>
                    </div>
                </DialogContent>
              )}
            </div>
            </Dialog>
          )}
          {/* Keep the form mounted so correcting an email preserves all fields. */}
          <div id="designer-application-form" tabIndex={-1} className="focus:outline-none">
            {!applyContext && (
              <div className="mt-4 flex justify-center">
                <Link
                  href="/portfolio-review"
                  className="group inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3 py-1 text-xs text-[#5A4F45] transition-colors hover:border-[#f98f2a] hover:text-[#231617]"
                >
                  <span className="inline-flex items-center rounded-full bg-[#F97316] px-1.5 text-[9px] font-semibold uppercase tracking-wide text-white leading-[1.4]">
                    New
                  </span>
                  Get a hiring manager&apos;s read on your portfolio. Free 15 min
                  <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            )}
            <DesignerApplicationForm
              onEmailChange={setEmail}
              duplicateEmail={duplicateEmail}
              applyCode={applyContext?.code}
              jdVersionId={applyContext?.jdVersionId ?? undefined}
              jobTitle={applyContext?.jobTitle}
            />
          </div>
        </div>
      </div>
    </HomePageLayout>
  );
}

export default function ApplicationPage({ applyContext }: ApplicationPageProps) {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-wcd-pricing flex items-center justify-center">
          <div className="text-gray-500">Loading...</div>
        </div>
      }
    >
      <ApplicationPageContent applyContext={applyContext} />
    </Suspense>
  );
}
